const MET_STATUSES = new Set([
  'met',
  'compliant',
  'on_track',
  'achieved',
  'green',
  'high_green',
  'medium_green',
  'low_green'
]);

const RISK_STATUSES = new Set([
  'not_met',
  'non_compliant',
  'at_risk',
  'below_target',
  'red',
  'gap'
]);

export const hasNumber = (value) => value !== null && value !== undefined && Number.isFinite(Number(value));

export const localizedValue = (item = {}, language = 'en', key = 'label') => {
  const arabicKeys = [`${key}_ar`, `${key}Ar`];
  if (language === 'ar') {
    const arabic = arabicKeys.map((candidate) => item?.[candidate]).find(Boolean);
    if (arabic) return arabic;
  }
  return item?.[key] || item?.[`${key}_en`] || item?.[`${key}En`] || '';
};

export const getStatusTone = (statusCode = '') => {
  const normalized = String(statusCode || '').trim().toLowerCase();
  if (MET_STATUSES.has(normalized)) return 'success';
  if (RISK_STATUSES.has(normalized)) return 'danger';
  if (['warning', 'provisional', 'incomplete'].includes(normalized)) return 'warning';
  return 'neutral';
};

export const getResultMetric = (result = {}) => {
  const resultType = String(result?.result_type || result?.resultType || 'percentage').toLowerCase();

  if (resultType === 'count' || resultType === 'absolute_count') {
    return {
      resultType: 'count',
      current: hasNumber(result.current_count) ? Number(result.current_count) : null,
      required: hasNumber(result.required_count) ? Number(result.required_count) : null,
      suffix: ''
    };
  }

  if (resultType === 'band') {
    return {
      resultType: 'band',
      current: result.official_band || result.band || result.status_code || null,
      required: result.required_band || null,
      suffix: ''
    };
  }

  return {
    resultType: 'percentage',
    current: hasNumber(result.legal_rate_pct)
      ? Number(result.legal_rate_pct)
      : hasNumber(result.current_rate_pct)
        ? Number(result.current_rate_pct)
        : null,
    required: hasNumber(result.required_rate_pct) ? Number(result.required_rate_pct) : null,
    suffix: '%'
  };
};

export const getProgressPercent = (metric = {}) => {
  if (!hasNumber(metric.current) || !hasNumber(metric.required) || Number(metric.required) <= 0) return null;
  return Math.min(100, Math.max(0, (Number(metric.current) / Number(metric.required)) * 100));
};

export const formatMetric = (value, suffix = '', maximumFractionDigits = 1, language = 'en') => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'string') return value;
  if (!Number.isFinite(Number(value))) return '—';
  const locale = String(language || '').toLowerCase().startsWith('ar') ? 'ar' : 'en-US';
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits }).format(Number(value))}${suffix}`;
};

export const formatDate = (value, language = 'en') => {
  if (!value) return '—';
  const raw = String(value);
  const dateValue = /^\d{4}-\d{2}-\d{2}$/.test(raw)
    ? new Date(`${raw}T00:00:00`)
    : new Date(raw);
  if (Number.isNaN(dateValue.getTime())) return raw;
  const locale = String(language || '').toLowerCase().startsWith('ar') ? 'ar' : 'en-US';
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }).format(dateValue);
};

export const getUniqueRuleSources = (overview = {}) => {
  const seen = new Set();
  return (Array.isArray(overview?.obligations) ? overview.obligations : [])
    .flatMap((obligation) => {
      const sources = [
        ...(Array.isArray(obligation?.sources) ? obligation.sources : []),
        ...(obligation?.source ? [obligation.source] : [])
      ];
      return sources.map((source) => ({
        ...source,
        ruleId: obligation?.rule_id,
        ruleVersion: obligation?.rule_version,
        effectiveFrom: obligation?.effective_from,
        effectiveTo: obligation?.effective_to,
        deadline: obligation?.deadline
      }));
    })
    .filter((source) => {
      const key = `${source?.url || ''}:${source?.reference || ''}:${source?.ruleId || ''}`;
      if (!key.replace(/:/g, '')) return false;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

export const isProfileReady = (profileEnvelope = {}) => {
  const profile = profileEnvelope?.profile || profileEnvelope || {};
  const establishments = Array.isArray(profile?.establishments) ? profile.establishments : [];
  const missingFields = Array.isArray(profileEnvelope?.missing_fields) ? profileEnvelope.missing_fields : [];
  return Boolean(profile?.country_code && establishments.length > 0 && missingFields.length === 0);
};
